import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  useNavigate,
  useParams
} from 'react-router-dom';

import './AdminPartnerForm.scss';

const DEFAULT_FORM = {
  partner_type: 'COURSE',
  organization_name: '',
  status: 'PROSPECT',

  contact_name: '',
  contact_title: '',
  contact_email: '',
  contact_phone: '',

  website: '',
  course_id: '',

  last_contact_at: '',
  next_follow_up_at: '',

  notes: ''
};

const toDateInput = (
  value
) => {
  if (!value) {
    return '';
  }

  const date =
    new Date(value);

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1
    ).padStart(
      2,
      '0'
    );

  const day =
    String(
      date.getDate()
    ).padStart(
      2,
      '0'
    );

  return `${year}-${month}-${day}`;
};

export default function AdminPartnerForm() {
  const navigate =
    useNavigate();

  const {
    id
  } = useParams();

  const isEditing =
    Boolean(id);

  const apiBaseUrl =
    useMemo(() => {
      const envUrl =
        process.env
          .REACT_APP_API_URL ||
        '';

      return envUrl.endsWith('/')
        ? envUrl.slice(
            0,
            -1
          )
        : envUrl;
    }, []);

  const token =
    localStorage.getItem(
      'tg_admin_token'
    );

  const [
    form,
    setForm
  ] = useState(
    DEFAULT_FORM
  );

  const [
    partnerTypes,
    setPartnerTypes
  ] = useState([]);

  const [
    partnerStatuses,
    setPartnerStatuses
  ] = useState([]);

  const [
    courses,
    setCourses
  ] = useState([]);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  useEffect(() => {
    const initialize =
      async () => {
        setLoading(true);
        setError('');

        try {
          const metaResponse =
            await fetch(
              `${apiBaseUrl}/admin/partners/meta`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const meta =
            await metaResponse.json();

          if (!metaResponse.ok) {
            throw new Error(
              meta?.message ||
                'Unable to load partner options.'
            );
          }

          setPartnerTypes(
            meta.partnerTypes || []
          );

          setPartnerStatuses(
            meta.partnerStatuses ||
              []
          );

          setCourses(
            meta.courses || []
          );

          if (isEditing) {
            const response =
              await fetch(
                `${apiBaseUrl}/admin/partners/${id}`,
                {
                  headers: {
                    Authorization:
                      `Bearer ${token}`
                  }
                }
              );

            const data =
              await response.json();

            if (!response.ok) {
              throw new Error(
                data?.message ||
                  'Unable to load partner.'
              );
            }

            setForm({
              ...DEFAULT_FORM,
              ...data.partner,

              course_id:
                data.partner.course_id ||
                '',

              last_contact_at:
                toDateInput(
                  data.partner.last_contact_at
                ),

              next_follow_up_at:
                toDateInput(
                  data.partner.next_follow_up_at
                )
            });
          }
        } catch (err) {
          setError(
            err.message ||
              'Unable to initialize partner form.'
          );
        } finally {
          setLoading(false);
        }
      };

    initialize();
  }, [
    apiBaseUrl,
    id,
    isEditing,
    token
  ]);

  const handleChange = (
    event
  ) => {
    const {
      name,
      value
    } = event.target;

    setForm(
      (previous) => ({
        ...previous,
        [name]: value
      })
    );
  };

  const handleSubmit =
    async (event) => {
      event.preventDefault();

      if (
        !form.organization_name
          .trim()
      ) {
        setError(
          'Organization name is required.'
        );

        return;
      }

      setSaving(true);
      setError('');

      try {
        const payload = {
          ...form,

          course_id:
            form.course_id
              ? Number(
                  form.course_id
                )
              : null,

          last_contact_at:
            form.last_contact_at
              ? new Date(
                  `${form.last_contact_at}T12:00:00`
                ).toISOString()
              : null,

          next_follow_up_at:
            form.next_follow_up_at
              ? new Date(
                  `${form.next_follow_up_at}T12:00:00`
                ).toISOString()
              : null
        };

        const url =
          isEditing
            ? `${apiBaseUrl}/admin/partners/${id}`
            : `${apiBaseUrl}/admin/partners`;

        const response =
          await fetch(
            url,
            {
              method:
                isEditing
                  ? 'PATCH'
                  : 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`
              },

              body:
                JSON.stringify(
                  payload
                )
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to save partner.'
          );
        }

        navigate(
          `/admin/partners/${data.partner.partner_id}`
        );
      } catch (err) {
        setError(
          err.message ||
            'Unable to save partner.'
        );
      } finally {
        setSaving(false);
      }
    };

  if (loading) {
    return (
      <div className="admin-partner-form-loading">
        Loading partner...
      </div>
    );
  }

  return (
    <div className="admin-partner-form-page">

      <section className="admin-partner-form-heading">

        <div>
          <p>
            Relationship Management
          </p>

          <h2>
            {isEditing
              ? 'Edit Partner'
              : 'Add Partner'}
          </h2>

          <span>
            Track outreach,
            relationships and
            follow-ups.
          </span>
        </div>

        <button
          type="button"
          onClick={() =>
            navigate(
              isEditing
                ? `/admin/partners/${id}`
                : '/admin/partners'
            )
          }
        >
          ← Back
        </button>

      </section>

      <form
        className="admin-partner-form"
        onSubmit={
          handleSubmit
        }
      >

        {error ? (
          <div className="admin-partner-form__error">
            {error}
          </div>
        ) : null}

        <section className="admin-partner-form-card">

          <div className="admin-partner-form-card__header">
            <span>
              01
            </span>

            <div>
              <h3>
                Organization
              </h3>

              <p>
                Relationship type
                and pipeline status.
              </p>
            </div>
          </div>

          <div className="admin-partner-form-grid">

            <div className="admin-partner-form-field admin-partner-form-field--full">

              <label>
                Organization Name *
              </label>

              <input
                name="organization_name"
                value={
                  form.organization_name
                }
                onChange={
                  handleChange
                }
                placeholder="Royal Ontario Golf Club"
              />

            </div>

            <div className="admin-partner-form-field">

              <label>
                Type
              </label>

              <select
                name="partner_type"
                value={
                  form.partner_type
                }
                onChange={
                  handleChange
                }
              >
                {partnerTypes.map(
                  (type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {type}
                    </option>
                  )
                )}
              </select>

            </div>

            <div className="admin-partner-form-field">

              <label>
                Status
              </label>

              <select
                name="status"
                value={
                  form.status
                }
                onChange={
                  handleChange
                }
              >
                {partnerStatuses.map(
                  (status) => (
                    <option
                      key={status}
                      value={status}
                    >
                      {status}
                    </option>
                  )
                )}
              </select>

            </div>

            <div className="admin-partner-form-field admin-partner-form-field--full">

              <label>
                Related Course
              </label>

              <select
                name="course_id"
                value={
                  form.course_id
                }
                onChange={
                  handleChange
                }
              >
                <option value="">
                  No linked course
                </option>

                {courses.map(
                  (course) => (
                    <option
                      key={
                        course.course_id
                      }
                      value={
                        course.course_id
                      }
                    >
                      {course.course_name}
                      {course.city
                        ? ` — ${course.city}`
                        : ''}
                    </option>
                  )
                )}
              </select>

            </div>

          </div>
        </section>

        <section className="admin-partner-form-card">

          <div className="admin-partner-form-card__header">
            <span>
              02
            </span>

            <div>
              <h3>
                Contact
              </h3>

              <p>
                Primary relationship
                contact.
              </p>
            </div>
          </div>

          <div className="admin-partner-form-grid">

            <div className="admin-partner-form-field">
              <label>
                Contact Name
              </label>

              <input
                name="contact_name"
                value={
                  form.contact_name
                }
                onChange={
                  handleChange
                }
              />
            </div>

            <div className="admin-partner-form-field">
              <label>
                Title
              </label>

              <input
                name="contact_title"
                value={
                  form.contact_title
                }
                onChange={
                  handleChange
                }
                placeholder="General Manager"
              />
            </div>

            <div className="admin-partner-form-field">
              <label>
                Email
              </label>

              <input
                type="email"
                name="contact_email"
                value={
                  form.contact_email
                }
                onChange={
                  handleChange
                }
              />
            </div>

            <div className="admin-partner-form-field">
              <label>
                Phone
              </label>

              <input
                name="contact_phone"
                value={
                  form.contact_phone
                }
                onChange={
                  handleChange
                }
              />
            </div>

            <div className="admin-partner-form-field admin-partner-form-field--full">
              <label>
                Website
              </label>

              <input
                name="website"
                value={
                  form.website
                }
                onChange={
                  handleChange
                }
                placeholder="https://..."
              />
            </div>

          </div>
        </section>

        <section className="admin-partner-form-card">

          <div className="admin-partner-form-card__header">
            <span>
              03
            </span>

            <div>
              <h3>
                Follow-Up
              </h3>

              <p>
                Keep relationship
                activity organized.
              </p>
            </div>
          </div>

          <div className="admin-partner-form-grid">

            <div className="admin-partner-form-field">
              <label>
                Last Contact
              </label>

              <input
                type="date"
                name="last_contact_at"
                value={
                  form.last_contact_at
                }
                onChange={
                  handleChange
                }
              />
            </div>

            <div className="admin-partner-form-field">
              <label>
                Next Follow-Up
              </label>

              <input
                type="date"
                name="next_follow_up_at"
                value={
                  form.next_follow_up_at
                }
                onChange={
                  handleChange
                }
              />
            </div>

            <div className="admin-partner-form-field admin-partner-form-field--full">

              <label>
                Notes
              </label>

              <textarea
                name="notes"
                rows="7"
                value={
                  form.notes
                }
                onChange={
                  handleChange
                }
                placeholder="Conversation notes, partnership ideas, follow-up details..."
              />

            </div>

          </div>
        </section>

        <div className="admin-partner-form-actions">

          <button
            type="button"
            className="secondary"
            onClick={() =>
              navigate(
                '/admin/partners'
              )
            }
          >
            Cancel
          </button>

          <button
            type="submit"
            className="primary"
            disabled={
              saving
            }
          >
            {saving
              ? 'Saving...'
              : isEditing
              ? 'Save Changes'
              : 'Create Partner'}
          </button>

        </div>

      </form>

    </div>
  );
}